<template>
  <div class="study">
    <snackbar v-model="snackbar">Study has been updated</snackbar>
    <breadcrumbs
      v-if="study.title"
      :items="breadcrumbs"
      :active="study.title"
    />
    <main class="study__main">
      <text-field
        class="mt0"
        type="single"
        name="title"
        label="Title"
        v-model="draft.title"
        size="lg"
      />
      <span class="title">Description</span>
      <markdown-editor v-if="loaded" v-model="draft.description" />
    </main>
    <div class="study__sidebar">
      <app-button
        class="mb-4"
        variant="outline"
        :to="{ name: 'art-club-study', params: { id: this.$route.params.id } }"
        >View study</app-button
      >

      <card title="Status">
        <ul class="study__status">
          <li>
            Status:
            <span :class="study.published ? 'success' : 'warning'">
              {{ status }}
            </span>
          </li>
          <li>
            Deadline:
            <datepicker
              class="datepicker"
              :use-utc="true"
              v-model="draft.deadline"
            />
          </li>
        </ul>

        <div class="status-actions">
          <app-button
            size="sm"
            :disabled="!hasChanges"
            @click.native="saveChanges"
            >Save changes</app-button
          >
          <app-button
            @click.native="publishHandler"
            v-if="!study.published"
            variant="cta"
            size="sm"
            >Publish study</app-button
          >
          <app-button
            size="sm"
            variant="outline"
            @click.native="unpublishHandler"
            v-else
            >Unpublish study</app-button
          >
        </div>
      </card>

      <card title="References">
        <references v-model="draft.references" />
      </card>
    </div>
  </div>
</template>

<script>
import TextField from '@/components/form/TextField.vue';
import Card from '@/components/global/Card.vue';
import MarkdownEditor from '@/components/editor/MarkdownEditor';
import Breadcrumbs from '@/components/app/Breadcrumbs.vue';
import References from '@/components/admin/study/References.vue';
import { mapActions } from 'vuex';
import Datepicker from 'vuejs-datepicker';

export default {
  name: 'admin-study',
  data() {
    return {
      loaded: false,
      breadcrumbs: [
        {
          title: 'Admin',
          route: 'admin',
        },
        {
          title: 'Art Club',
          route: 'admin-studies',
        },
      ],
      snackbar: false,
      study: {},
      draft: {
        title: '',
        description: '',
        deadline: '',
        references: '',
      },
      isShowingDatepicker: false,
    };
  },
  components: {
    TextField,
    Breadcrumbs,
    Card,
    References,
    MarkdownEditor,
    Datepicker,
  },
  async mounted() {
    this.loadStudy();
  },
  computed: {
    status() {
      return this.study.published ? 'Published' : 'Draft';
    },
    sanitized() {
      return this.draft.description.toString();
    },
    hasChanges() {
      return (
        this.study.title !== this.draft.title ||
        this.study.description !== this.draft.description ||
        this.study.deadline !== this.draft.deadline ||
        this.study.references !== this.draft.references
      );
    },
  },
  methods: {
    ...mapActions('studies', [
      'getStudy',
      'patchStudy',
      'publishStudy',
      'unpublishStudy',
      'deleteStudy',
    ]),
    async loadStudy() {
      const response = await this.getStudy(this.$route.params.id);
      this.study = JSON.parse(JSON.stringify(response));
      this.draft = JSON.parse(JSON.stringify(response));
      this.loaded = true;
    },
    async saveChanges() {
      let deadline = this.draft.deadline;
      deadline.setUTCHours(15);
      const payload = {
        id: this.$route.params.id,
        study: {
          title: this.draft.title.toString(),
          description: this.draft.description.toString(),
          deadline: deadline,
          references: this.draft.references,
        },
      };
      const res = await this.patchStudy(payload);
      if (res.error) {
        // console.log(res.error);
      } else {
        this.study = JSON.parse(JSON.stringify(res));
        this.draft = JSON.parse(JSON.stringify(res));

        this.snackbar = true;
        setTimeout(() => {
          this.snackbar = false;
        }, 3000);
      }
    },
    async publishHandler() {
      if (this.hasChanges) {
        await this.saveChanges();
      }

      this.publishStudy(this.$route.params.id)
        .then(() => {
          this.snackbar = true;
          this.loadStudy();
          setTimeout(() => {
            this.snackbar = false;
          }, 3000);
        })
        .catch(() => {
          // console.log(err);
        });
    },
    async unpublishHandler() {
      try {
        await this.unpublishStudy(this.$route.params.id);
        this.loadStudy();
      } catch (err) {
        // console.log(err);
      }
    },
  },
};
</script>

<style lang="scss">
.datepicker {
  input[type='text'] {
    display: flex;
    flex-direction: column;
    background: color('background', 'light');
    border: 1px solid color('grey');
    border-radius: 8px;
    padding: 4px 12px;
    margin: 4px 0 8px;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    transition: all 128ms ease-in-out;
  }
}
</style>

<style lang="scss" scoped>
.study {
  display: grid;
  grid-column-gap: 24px;
  grid-template-columns: 2fr 1fr;
  padding: 0 24px;

  .breadcrumbs {
    grid-column: span 2;
  }

  &__status {
    list-style: none;
    padding: 0;
  }

  .status-actions {
    display: flex;
    justify-content: space-between;
  }
}
</style>
